import { accountAccessShowLogin } from 'actions/UiActions'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'LuxKit/Button/TextButton'
import LinePricetagAltIcon from 'LuxKit/Icons/line/LinePricetagAltIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferTilePricing from 'components/OfferList/OfferListTiles/Support/OfferTilePricing'
import SoldOutMessage from 'components/OfferList/OfferListTiles/Support/SoldOutMessage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import type moment from 'moment'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import OfferTilePropertyHeading from '../../../OfferList/OfferListTiles/SearchOfferTiles/TileSections/OfferTilePropertyHeading'
import { EmptyObject } from 'lib/object/objectUtils'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useHideAnytimePrices from 'hooks/Offers/useHideAnytimePrices'
import { isHotel } from 'lib/offer/offerTypes'
import SearchMapCardLayout from './SearchMapCardLayout'

const Container = styled(Group)`
  padding: ${rem(12)};
`

interface Props {
  onClose?: () => void
  offer: App.Offer | App.OfferSummary
  checkInDate?: moment.Moment
  filters?: App.OfferListFilters
  offerUrl: string
  bestPricePackage?: App.Package
  bestPriceForDates?: App.OfferAvailableRate
  fetchingPrice: boolean
}

function LeSearchMapCard(props: Props) {
  const {
    onClose,
    offer,
    checkInDate,
    filters = EmptyObject,
    offerUrl,
    bestPricePackage,
    bestPriceForDates,
    fetchingPrice,
  } = props
  const dispatch = useAppDispatch()
  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')
  const offerMetaData = useOfferMetaData(offer.id, filters)
  const { images, type, property } = offer
  const { onAnyHotelSelectDatesPrompt } = useContext(GlobalSearchStateContext)

  const displayImages = useMemo(() => images.length && !isTabletScreenSizeAndUp ? [images[0]] : images, [images, isTabletScreenSizeAndUp])

  const loginHandler = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    dispatch(accountAccessShowLogin())
  }, [dispatch])

  const handleSelectDatesClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onAnyHotelSelectDatesPrompt && isHotel(offer)) {
      onAnyHotelSelectDatesPrompt(offer)
    }
  }, [onAnyHotelSelectDatesPrompt, offer])

  const hidePrice = useHideAnytimePrices(offer.type, offerMetaData?.hidePricing)

  const intl = useIntl()

  const membersExclusiveRatesText = intl.formatMessage({
    id: 'F8JEQV8tcd8esUiyYErP',
    defaultMessage: 'Members get exclusive rates',
    description: 'Walled content message encouraging members to view exclusive rates',
  })

  const logInText = intl.formatMessage({
    id: 'FgWg-IrUqHF5kLe_R4JH',
    defaultMessage: 'Log In',
    description: 'Action button label for login call-to-action on the map card',
  })

  const signUpForFreeText = intl.formatMessage({
    id: 'JZZkB6iTlHcgbjW3OZm-',
    defaultMessage: 'Sign up for free',
    description: 'Button text for sign-up call-to-action on map card',
  })

  const viewOfferText = intl.formatMessage({
    id: 'g2xmD1R2ECRvmhSLV_yx',
    defaultMessage: 'View offer',
    description: 'Button text to view offer details on mobile map card',
  })

  const selectDatesText = intl.formatMessage({
    id: '0cI9WcelLhiPKVrBtRAQ',
    defaultMessage: 'Select dates',
    description: 'Button text shown when prices are hidden, prompting user to select dates',
  })

  const shouldRenderPricing = !!bestPricePackage && !fetchingPrice
  const shouldRenderSoldOut = !(bestPriceForDates || bestPricePackage) && !fetchingPrice

  return (
    <SearchMapCardLayout
      images={displayImages}
      onClose={onClose}
      bookmarkButton={<BookmarkButton offer={offer} withLabel="never" size="small" iconKind="primary" iconVariant="ghost" />}
      imageBottomLeft={
        <CSSBreakpoint max="mobile" as={LabelGroup}>
          {type === 'hotel' && <ProductTypeLabel kind="default" productType="hotel" />}
          <OfferPartnershipLabel offer={offer} />
        </CSSBreakpoint>
      }
    >
      <Container direction="vertical" gap={hidePrice ? 16 : 8}>
        <Group direction="horizontal" horizontalAlign="space-between" gap={4} verticalAlign="start">
          <Group direction="vertical" gap={4}>
            {property && <OfferTilePropertyHeading name={property.name} />}
            <BodyText variant="medium" colour="neutral-two" lineClamp={2}>{offer.name}</BodyText>
            <CSSBreakpoint min="tablet">
              {type === 'hotel' && <ProductTypeLabel productType={offer.productType} kind="plain" />}
            </CSSBreakpoint>
          </Group>
          {isOfferRatingDisplayable(property?.rating) && <OfferRating variant="xs" rating={property.rating} onlyNumber />}
        </Group>
        <ProductPaletteProvider product={offer}>
          <WalledContent
            enforced={offer.walledGarden}
            wall={
              <>
                <CaptionTextBlock
                  variant="medium"
                  weight="bold"
                  startIcon={<LinePricetagAltIcon />}
                >
                  {membersExclusiveRatesText}
                </CaptionTextBlock>
                <Group gap={8} direction="horizontal">
                  <TextButton
                    kind="secondary"
                    onClick={loginHandler}
                    size="small"
                  >
                    {logInText}
                  </TextButton>
                  <TextButton kind="primary" onClick={loginHandler} size="small">
                    {signUpForFreeText}
                  </TextButton>
                </Group>
              </>
            }
          >
            <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
              {shouldRenderPricing &&
                <>
                  <OfferTilePricing
                    offer={offer}
                    hasDates={!!checkInDate}
                    checkInDate={checkInDate}
                    pkg={bestPricePackage}
                    offerUrl={offerUrl}
                    align="start"
                    availableRate={bestPriceForDates}
                    filters={filters}
                    showCompact
                    hideIncludesTaxesAndFees
                    hidePrice={hidePrice}
                  />
                  {!hidePrice && <CSSBreakpoint max="mobile">
                    <TextButton kind="primary">{viewOfferText}</TextButton>
                  </CSSBreakpoint>}
                </>}
              {shouldRenderSoldOut &&
                <div>
                  <SoldOutMessage offer={offer} filters={filters} />
                </div>}
            </Group>
            {hidePrice &&
              <TextButton kind="primary" fit="full-width" onClick={handleSelectDatesClick}>
                {selectDatesText}
              </TextButton>}
          </WalledContent>
        </ProductPaletteProvider>
      </Container>
    </SearchMapCardLayout>
  )
}

export default LeSearchMapCard
